/**
 * Czysty stan chodu (path / velocity) — bez Phaser.
 */
import { stepToward } from './walkCollision.js';
import {
  DEFAULT_AGENT_RADIUS,
  DEFAULT_WALK_SPEED,
  type ColliderRect,
  type WorldMoveBounds,
} from './types.js';

export type WalkControllerState = {
  x: number;
  y: number;
  tx: number;
  ty: number;
  /** Tryb: 'path' = idzie do tx/ty; 'velocity' = ciągła prędkość z klawiatury/hold. */
  mode: 'path' | 'velocity' | 'idle';
  vx: number;
  vy: number;
};

/**
 * Czysty krok kontrolera — do testów i wspólnej logiki.
 */
export function stepWalkState(
  state: WalkControllerState,
  dtSec: number,
  colliders: readonly ColliderRect[],
  opts: {
    speed?: number;
    agentRadius?: number;
    bounds?: WorldMoveBounds;
  } = {},
): WalkControllerState {
  const speed = opts.speed ?? DEFAULT_WALK_SPEED;
  const radius = opts.agentRadius ?? DEFAULT_AGENT_RADIUS;
  const bounds = opts.bounds;
  const dt = Math.min(Math.max(dtSec, 0), 0.1);

  if (state.mode === 'velocity') {
    const len = Math.hypot(state.vx, state.vy);
    if (len < 1e-6) {
      return { ...state, mode: 'idle', vx: 0, vy: 0 };
    }
    const nx = state.vx / len;
    const ny = state.vy / len;
    const toX = state.x + nx * speed * dt;
    const toY = state.y + ny * speed * dt;
    const next = stepToward(
      state.x,
      state.y,
      toX,
      toY,
      dt,
      speed,
      colliders,
      radius,
      bounds,
    );
    const mvx = (next.x - state.x) / (dt || 1e-6);
    const mvy = (next.y - state.y) / (dt || 1e-6);
    return {
      ...state,
      x: next.x,
      y: next.y,
      tx: next.x,
      ty: next.y,
      vx: mvx,
      vy: mvy,
      mode: Math.hypot(mvx, mvy) < 1 ? 'idle' : 'velocity',
    };
  }

  if (state.mode === 'path') {
    const next = stepToward(
      state.x,
      state.y,
      state.tx,
      state.ty,
      dt,
      speed,
      colliders,
      radius,
      bounds,
    );
    const mvx = (next.x - state.x) / (dt || 1e-6);
    const mvy = (next.y - state.y) / (dt || 1e-6);
    const remaining = Math.hypot(state.tx - next.x, state.ty - next.y);
    // Zablokowany: prawie zero ruchu, a cel wciąż daleko (np. za ścianą)
    const blocked = !next.arrived && remaining > 2 && Math.hypot(mvx, mvy) < 0.5;
    if (next.arrived || blocked) {
      return {
        ...state,
        x: next.x,
        y: next.y,
        vx: 0,
        vy: 0,
        mode: 'idle',
        tx: blocked ? state.tx : next.x,
        ty: blocked ? state.ty : next.y,
      };
    }
    return {
      ...state,
      x: next.x,
      y: next.y,
      vx: mvx,
      vy: mvy,
      mode: 'path',
    };
  }

  return { ...state, vx: 0, vy: 0 };
}
